import socketIoClient from 'socket.io-client';
import swal from 'sweetalert2';
import Route from '../Routes';
import store from '../store';
import * as storeActions from '../store/actions';
import CompanyService from './CompanyService';
import UrlService from './UrlService';

const DEFAULT_RETRY_ATTEMPTS = 0; //3;

class HttpService {
  private defaultHeaders: any = {};

  setDefaultHeader(name: string, value: any): void {
    this.defaultHeaders[name] = value;
  }

  removeDefaultHeader(name: string): void {
    delete this.defaultHeaders[name];
  }

  get(
    url: string,
    /*data: any = {}, */ headers: any = {},
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    return this.send(
      {
        method: 'GET',
        url,
        headers,
        // body: JSON.stringify(data),
      },
      requestRetryAttemptsCount
    );
  }

  post(
    url: string,
    data: any = {},
    headers: any = {},
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    return this.send(
      {
        method: 'POST',
        url,
        headers,
        body: JSON.stringify(data),
      },
      requestRetryAttemptsCount
    );
  }

  put(
    url: string,
    data: any = {},
    headers: any = {},
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    return this.send(
      {
        method: 'PUT',
        url,
        headers,
        body: JSON.stringify(data),
      },
      requestRetryAttemptsCount
    );
  }

  delete(
    url: string,
    data: any = {},
    headers: any = {},
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    return this.send(
      {
        method: 'DELETE',
        url,
        headers,
        body: JSON.stringify(data),
      },
      requestRetryAttemptsCount
    );
  }

  patch(
    url: string,
    data: any = {},
    headers: any = {},
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    return this.send(
      {
        method: 'PATCH',
        url,
        headers,
        body: JSON.stringify(data),
      },
      requestRetryAttemptsCount
    );
  }

  send(requestInfo: any, requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS): Promise<Response> {
    return this.sendFetch(requestInfo, false, true, requestRetryAttemptsCount);
  }

  withFormData(
    method: string,
    url: string,
    data: FormData = null,
    headers: any = {},
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    const options = {
      method,
      url,
      headers,
      body: data,
    };

    return this.sendFetch(options, true, true, requestRetryAttemptsCount);
  }

  otherSite(url: string, requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS): Promise<Response> {
    const options = {
      method: 'GET',
      url,
    };

    return this.sendFetch(options, true, false, requestRetryAttemptsCount);
  }

  getApiHost(): string {
    return this.withCompanyName(process.env.REACT_APP_API_ENDPOINT);
  }

  getSocketHost(): string {
    return this.withCompanyName(process.env.REACT_APP_SOCKET_ENDPOINT);
  }

  withCompanyName(domainTemplate?: Nullable<string>): string {
    let domain = (domainTemplate || '').replace('[[COMPANY_NAME]]', CompanyService.getShortName());
    if (domain.endsWith('/')) {
      domain = domain.substr(0, domain.length - 1);
    }

    return domain;
  }

  open(url: string): void {
    const reqUrl = url[0] == '/' ? url.substr(1) : url;
    UrlService.openTab(`${this.getApiEndpoint()}${reqUrl}`);
  }

  getAppDomain() {
    const { protocol, hostname, port } = window.location;

    let domain = `${protocol}//${hostname}`;
    if (port) {
      domain = `${domain}:${port}`;
    }

    return domain;
  }

  getApiEndpoint() {
    const lang = localStorage.getItem('i18nextLng') || 'en-US';
    return this.getApiHost() + '/api/application/' + lang + '/';
  }

  private sendFetch(
    requestInfo: any,
    isFormData: boolean,
    isSame: boolean,
    requestRetryAttemptsCount = DEFAULT_RETRY_ATTEMPTS
  ): Promise<Response> {
    const { url } = requestInfo;
    delete requestInfo.url;

    requestInfo.headers = {
      // "Accept": "application/json",
      'X-Request-With': 'XMLHttpRequest',
      ...this.defaultHeaders,
      ...requestInfo.headers,
    };

    if (!isFormData) {
      requestInfo.headers['Content-Type'] = 'application/json';
    }

    requestInfo.redirect = 'follow';

    const handleAndNotifyHttpError = (error) => {
      if (error instanceof Response && error.status == 401) {
        store.dispatch({ type: storeActions.AUTH_LOGOUT });
        return Promise.reject(error);
      } else {
        if (requestRetryAttemptsCount > 0) {
          requestInfo.url = url;
          return this.sendFetch(requestInfo, isFormData, isSame, requestRetryAttemptsCount - 1);
        }

        let message = 'Something went wrong.';
        if (!['prod', 'production'].includes(process.env.NODE_ENV)) {
          const getErrorMessage = (error) => {
            if (error instanceof Error) {
              return error.message;
            }

            if (error instanceof Response) {
              return `Error ${error.status} - ${error.statusText}`;
            }

            return error.toString();
          };

          message = `Something went wrong with your request, Error:<br />${getErrorMessage(error)}`;
        }

        // swal.fire('Server error', message, 'error');
        return Promise.reject(error);
      }
    };

    try {
      const reqUrl = url[0] == '/' ? url.substr(1) : url;
      return fetch(`${isSame ? this.getApiEndpoint() : ''}${reqUrl}`, requestInfo)
        .then((response: Response) => {
          if (response.status < 400) {
            return response;
          } else if (response.status < 500) {
            requestRetryAttemptsCount = -1;
            throw response;
          }

          throw response;
        })
        .catch((error) => handleAndNotifyHttpError(error));
    } catch (error) {
      return handleAndNotifyHttpError(error);
    }
  }
}

export default new HttpService();
